import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";
import Slide from "@mui/material/Slide";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { styled } from "@mui/material/styles";
import React, { use, useEffect, useState } from "react";
import { usePaymentChannels } from "../hooks/usePaymentChannels";
import LaoQrPayment from "../components/payment/LaoQrPayment";
import { useAuthStore } from "../store/useAuthStore";
import CreditCardPayment from "../components/payment/CreditCardPayment";
import { OrderModel } from "../repositories/cart.repository";
import { useCredit } from "../hooks/useCredit";
import { useOrderStore } from "../store/useOrderStore";
import { useNavigate } from "react-router-dom";
import { handlePaymentSuccessService } from "../service/handlePaymentSuccess.service";
import { useCountdownStore } from "../store/useCountdownStore";
import { useSnackbarStore } from "../store/useSnackbarStore";
import { PaymentSocketResponse, SuccessData } from "../types/payment";
import { FormShell } from "../components/forms/FormCell";
import { SuccessReceipt } from "../components/payment/SuccessReceipt";
import PaymentFooter from "../components/payment/PaymentFooter";

const CREDIT_CARD_FORM_ID = "credit-card-form";

// Styled components for modern look
const StyledCard = styled(Card)<{ selected?: boolean }>(
  ({ theme, selected }) => ({
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: `2px solid ${
      selected ? theme.palette.primary.main : "transparent"
    }`,
    backgroundColor: selected
      ? theme.palette.primary.main + "08"
      : theme.palette.background.paper,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[8],
      borderColor: selected
        ? theme.palette.primary.main
        : theme.palette.primary.light,
    },
    position: "relative",
    overflow: "visible",
  }),
);

const PaymentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(4),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const CheckIconWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -8,
  right: -8,
  backgroundColor: theme.palette.primary.main,
  borderRadius: "50%",
  width: 24,
  height: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: theme.shadows[3],
}));

// Helper function to get appropriate icon for payment channel
const getPaymentIcon = (channel: string) => {
  const channelLower = channel.toLowerCase();

  // Specific payment channels
  if (channelLower.includes("laoqr")) {
    return <QrCodeIcon />;
  }
  if (channelLower.includes("debit") || channelLower.includes("credit")) {
    return <CreditCardIcon />;
  }
  if (channelLower.includes("m-money") || channelLower.includes("mmoney")) {
    return <AccountBalanceWalletIcon />;
  }

  // Default cash icon
  return <AttachMoneyIcon />;
};

interface PaymentStageProps {
  order: OrderModel;
}

const RedirectCountdownBox = ({
  isActive,
  secondsLeft,
}: {
  isActive: boolean;
  secondsLeft: number;
}) => {
  if (!isActive) return null;

  return (
    <Box
      sx={{
        width: "fit-content",
        padding: "8px 14px",
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "12px auto",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: "#333",
        }}
      >
        You’ll be redirected in <b>{secondsLeft}</b> seconds…
      </Typography>
    </Box>
  );
};
const PaymentStage: React.FC<PaymentStageProps> = ({ order }) => {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successData, setSuccessData] = useState<SuccessData | null>(null);
  const { showSnackbar } = useSnackbarStore();
  const { setSelectedChanelId } = useOrderStore.getState();

  const { data: paymentChannels, isLoading } = usePaymentChannels();
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false);
  const { user } = useAuthStore();
  const { makePaymentAsync, isPending: isMakingCreditPayment } = useCredit();
  const { isActive, secondsLeft } = useCountdownStore();

  // Driven by CreditCardPayment so the shared footer can render "Pay now"
  const [cardFooter, setCardFooter] = useState({
    canSubmit: false,
    processing: false,
  });

  const handleChannelSelect = (channelUuid: string) => {
    setSelectedChannel(channelUuid);
    setSelectedChanelId(channelUuid);
  };

  const handleProceedToPayment = () => {
    if (selectedChannel) {
      setShowPaymentForm(true);
    }
  };

  const handleBackToSelection = () => {
    setShowPaymentForm(false);
  };

  const { activeOrderId } = useOrderStore();

  const handlePaymentSuccess = (data: SuccessData) => {
    if (activeOrderId) handlePaymentSuccessService();
    setSuccessData(data);
  };

  const handlePaymentError = (error: string) => {
    // clearActiveOrderId(); clear if no retry on error
    setErrorMessage(error);
    showSnackbar("Payment failed. Please try again... " + error, "error");
  };

  const selectedChannelData = paymentChannels?.find(
    (channel) => channel.uuid === selectedChannel,
  );

  if (!!successData) {
    return (
      <div>
        {isActive && (
          <RedirectCountdownBox isActive={isActive} secondsLeft={secondsLeft} />
        )}
        <FormShell elevation={0} sx={{ border: "none", padding: 0 }}>
          <SuccessReceipt
            orderId={successData.orderId}
            amount={successData.amount}
            transactionId={successData.transactionId}
            date={successData.paidAt}
            paymentMethodData={successData.paymentMethodData}
          />
        </FormShell>
      </div>
    );
  }
  // Show Lao QR payment component if selected and proceeding

  if (
    showPaymentForm &&
    selectedChannelData?.channel.toLowerCase().includes("laoqr")
  ) {
    return (
      <Slide direction="left" in={showPaymentForm} mountOnEnter unmountOnExit>
        <Box>
          <Typography
            fontWeight="700"
            sx={{ mb: 3, textAlign: "center" }}
          >
            Complete Payment
          </Typography>

          {user && (
            <LaoQrPayment
              paymentChannelId={selectedChannelData.uuid}
              cusId={user?.cusId}
              orderId={order.orderId}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
              onCancel={handleBackToSelection}
            />
          )}

          <PaymentFooter
            cancelLabel="Back to Payment Methods"
            onCancel={handleBackToSelection}
          />
        </Box>
      </Slide>
    );
  }

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h5" fontWeight="700" sx={{ mb: 3 }}>
          Payment
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton
                    variant="circular"
                    width={40}
                    height={40}
                    sx={{ mb: 2 }}
                  />
                  <Skeleton variant="text" width="80%" height={24} />
                  <Skeleton variant="text" width="60%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>
        Choose Payment Method
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Select your preferred payment option to continue with the transaction
      </Typography>

      <FormControl component="fieldset" sx={{ width: "100%" }}>
        <RadioGroup
          value={selectedChannel}
          onChange={(e) => handleChannelSelect(e.target.value)}
        >
          <Grid container spacing={2}>
            {paymentChannels?.map((channel) => {
              const isDisabled = isMakingCreditPayment && isActive;

              return (
                <Grid item xs={12} sm={6} md={4} key={channel.uuid}>
                  <StyledCard
                    selected={selectedChannel === channel.uuid}
                    // Prevent click if disabled
                    onClick={() => {
                      if (!isDisabled) handleChannelSelect(channel.uuid);
                    }}
                    sx={{
                      opacity: isDisabled ? 0.5 : 1,
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    {selectedChannel === channel.uuid && (
                      <CheckIconWrapper>
                        <CheckCircleIcon
                          sx={{ color: "white", fontSize: 16 }}
                        />
                      </CheckIconWrapper>
                    )}

                    <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                      <FormControlLabel
                        value={channel.uuid}
                        control={<Radio sx={{ display: "none" }} />}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                mr: 2,
                                color:
                                  selectedChannel === channel.uuid
                                    ? "primary.main"
                                    : "text.secondary",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {getPaymentIcon(channel.channel)}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle1"
                                fontWeight={
                                  selectedChannel === channel.uuid ? 600 : 500
                                }
                                sx={{
                                  color:
                                    selectedChannel === channel.uuid
                                      ? "primary.main"
                                      : "text.primary",
                                }}
                              >
                                {channel.channel}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {(() => {
                                  const c = channel.channel.toLowerCase();
                                  if (c.includes("laoqr"))
                                    return "QR Code Payment";
                                  if (
                                    c.includes("debit") ||
                                    c.includes("credit")
                                  )
                                    return "Card Payment";
                                  if (
                                    c.includes("m-money") ||
                                    c.includes("mmoney")
                                  )
                                    return "Mobile Wallet";
                                  return "Cash Payment";
                                })()}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        disabled={isDisabled}
                        sx={{ m: 0, width: "100%" }}
                      />
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>
        </RadioGroup>
      </FormControl>

      {selectedChannelData && (
        <PaymentPaper elevation={2}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Box
              sx={{
                mr: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "primary.main",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {getPaymentIcon(selectedChannelData.channel)}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="600">
                {selectedChannelData.channel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete your payment using this method
              </Typography>
            </Box>
          </Box>

          {selectedChannelData.channel.toLowerCase().includes("laoqr") ? (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Click below to generate your QR code for payment
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<QrCodeIcon />}
                onClick={handleProceedToPayment}
                sx={{ minWidth: 200 }}
              >
                Generate QR Code
              </Button>
            </Box>
          ) : (
            <>
              <CreditCardPayment
                makePaymentAsync={makePaymentAsync}
                paymentChannelId={selectedChannelData.uuid}
                isActive={selectedChannelData.status === "inactive"}
                cusId={user?.cusId}
                order={order}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                onCancel={handleBackToSelection}
                isMakingCreditPayment={isMakingCreditPayment}
                formId={CREDIT_CARD_FORM_ID}
                onFooterStateChange={setCardFooter}
              />

              <PaymentFooter
                primary={{
                  label: cardFooter.processing ? "Processing…" : "Pay now",
                  type: "submit",
                  formId: CREDIT_CARD_FORM_ID,
                  disabled: !cardFooter.canSubmit,
                  loading: cardFooter.processing,
                }}
                onCancel={handleBackToSelection}
                cancelDisabled={cardFooter.processing}
              />
            </>
          )}
        </PaymentPaper>
      )}

      {paymentChannels?.length === 0 && (
        <Paper sx={{ p: 1, textAlign: "center", mt: 2 }}>
          <AttachMoneyIcon
            sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Payment Methods Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please contact support to set up payment methods.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PaymentStage;
